import { useState } from 'react';
import { useMediaQuery } from 'react-responsive';

import { TQuizAnswerData } from '@lib/core/quizzes/types';
import { localeV2 } from '@lib/tools/locale/source/pmi/v2';
import { LocaleFragment } from '@lib/tools/locale/views/LocaleFragment';
import { useHeight } from '@lib/tools/views/hooks';

import { Button } from '@components/pmi/src/atoms/CTA/Button';
import { HealthWarningFooterToIsrael } from '@components/pmi/src/common/HealthWarningFooterToIsrael';
import { useCurrentQuizBackground } from '@components/pmi/src/hooks/useCurrentQuizBackground';
import { Header } from '@components/pmi/src/organisms/Header/Header';
import { HealthWarningFooter } from '@components/pmi/src/organisms/HealthWarning/HealthWarning';
import { AnswerSectionHeader } from '@components/pmi/src/organisms/Quiz/AnswerSectionHeader/AnswerSectionHeader';
import { AnswerItem } from '@components/pmi/src/organisms/Quiz/QDS8001/AnswerItem/AnswerItem';
import { QuizFooter } from '@components/pmi/src/organisms/Quiz/QuizFooter/QuizFooter';

interface Props {
  isLight?: boolean;
  questionImage: string;
  questionTitle: string;
  questionCaption: string;
  isShowBackButton: boolean;
  isHealthWarningQuizAddon?: boolean;
  isHeetsBackgroundEnabled: boolean;
  isLoading: boolean;
  goBack: () => void;
  progressPercent: number;
  selectedChoicesCount: number;
  answers: TQuizAnswerData[];
  choicesQuantity: {
    maxChoices: number;
    minChoices: number;
  };
  handleConfirmAnswer: (answerId: string | string[], callback: () => void) => void;
  currentPrimaryColor?: string;
  isHideProgressBar?: boolean;
  isShowRestrictedBackground: boolean;
  isHealthWarningFooterToIsraelAddon?: boolean;
}

export const QuizPageQDS8001 = ({
  isLight = false,
  questionTitle,
  questionCaption,
  isLoading,
  questionImage,
  goBack,
  isShowBackButton,
  isHealthWarningQuizAddon,
  isHeetsBackgroundEnabled,
  progressPercent,
  answers,
  choicesQuantity,
  handleConfirmAnswer,
  currentPrimaryColor,
  isHideProgressBar = false,
  isShowRestrictedBackground,
  isHealthWarningFooterToIsraelAddon,
}: Props) => {
  const [answersArray, setAnswersArray] = useState<string[]>([]);
  const handleClick = () => {
    handleConfirmAnswer(answersArray, () => {
      setAnswersArray([]);
    });
  };
  const currentBackground = useCurrentQuizBackground(questionImage, isShowRestrictedBackground);
  const [footerRef, paddingForFooter] = useHeight<HTMLDivElement>();
  const minChoicesQuantity = choicesQuantity?.minChoices;
  const maxAnswersChoices = choicesQuantity?.maxChoices;
  const isMobileResolution = useMediaQuery({ maxWidth: 767 });
  const isNextButtonEnabled = answersArray.length >= minChoicesQuantity && maxAnswersChoices > 1;

  return (
    <div
      className={`ta-v2-qds8001-page ${isHealthWarningFooterToIsraelAddon ? 'padding-bottom-30vh minh-101vh' : 'minh-100vh'}`}
      style={{
        background: currentBackground,
        paddingBottom: paddingForFooter,
      }}
    >
      <Header
        caption={questionCaption}
        currentPrimaryColor={currentPrimaryColor}
        goBack={goBack}
        isHeetsBackgroundEnabled={isHeetsBackgroundEnabled}
        isHideProgressBar={isHideProgressBar}
        isLoading={isLoading}
        isShowBackButton={isShowBackButton}
        progressPercent={progressPercent}
        title={questionTitle}
      />
      <div className="ta-v2-qds8001-page-answer-section-cover">
        <div className="ta-v2-qds8001-page-answer-section">
          {answers?.length && (
            <AnswerSectionHeader
              isLight={isLight}
              maxChoicesCount={maxAnswersChoices}
              selectedChoicesCount={answersArray?.length || 0}
            />
          )}
          <div className="ta-v2-qds8001-page-answer-item-container">
            {answers?.map(answer => {
              const answerId = Object.keys(answer)[0];
              return (
                <AnswerItem
                  key={answerId}
                  answerId={answerId}
                  answerText={answer[answerId].text}
                  answersArray={answersArray}
                  className="ta-v2-qds8001-page-answer-item"
                  currentPrimaryColor={currentPrimaryColor}
                  handleConfirmAnswer={handleConfirmAnswer}
                  isLight={isLight}
                  maxAnswersChoices={maxAnswersChoices}
                  setAnswersArray={setAnswersArray}
                />
              );
            })}
          </div>
        </div>
        {!isMobileResolution && maxAnswersChoices > 1 ? (
          <Button
            btnText={<LocaleFragment message={localeV2.common.nextQuestion} />}
            className="ta-v2-cta-btn-margin"
            isDark={isNextButtonEnabled}
            isDisabled={!isNextButtonEnabled}
            onBtnClick={handleClick}
          />
        ) : null}
      </div>
      <div ref={footerRef} className="ta-v2-quiz-footer-wrapper">
        {isMobileResolution && maxAnswersChoices > 1 ? (
          <QuizFooter handleClick={handleClick} isNextButtonEnabled={isNextButtonEnabled} />
        ) : null}
        {isHealthWarningQuizAddon && <HealthWarningFooter />}
      </div>
      {isHealthWarningFooterToIsraelAddon && <HealthWarningFooterToIsrael />}
    </div>
  );
};
